import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Grid, 
  Slider, 
  Theme, 
  Tooltip, 
  Typography 
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Info } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { MocaScore } from "../types/UserProfile";
import Q1 from "./../image/Q1.jpg";
import Q2 from "./../image/Q2.jpg";
import Q3 from "./../image/Q3.jpg";
import Q4 from "./../image/Q4.jpg";
import Q5 from "./../image/Q5.jpg";
import Q6 from "./../image/Q6.jpg";
import Q7 from "./../image/Q7.jpg";

export const EditMocaScorePanel = (props: { initialValue?: MocaScore | undefined, open: boolean, onClose: () => void, onConfirm: (scores: MocaScore | null) => void }) => {

  const { initialValue } = props;
  const {t} = useTranslation();
  const [loaded, setLoaded] = useState<boolean>(false);
  const [scores, setScores] = useState<MocaScore>(
    {
      visuospatialExecutive: null,
      naming: null,
      attention: null,
      language: null,
      abstraction: null,
      delayedRecall: null,
      orientation: null
    }
  );

  useEffect(() => {
    if (initialValue && !loaded) {
      setScores(initialValue);
    }
  }, [initialValue])

  const marks = [
    {
      value: -1,
      label: t("editMocaScorePanel.marks_not_set"),
    },
    {
      value: 0,
      label: `0${t("common.score_empty")}`,
    },
    {
      value: 1,
      label: `1${t("common.score_empty")}`,
    },
    {
      value: 2,
      label: `2${t("common.score_empty")}`,
    },
    {
      value: 3,
      label: `3${t("common.score_empty")}`,
    },
    {
      value: 4,
      label: `4${t("common.score_empty")}`,
    },
    {
      value: 5,
      label: `5${t("common.score_empty")}`,
    },
    {
      value: 6,
      label: `6${t("common.score_empty")}`,
    },
    {
      value: 7,
      label: `7${t("common.score_empty")}`,
    },
  ];

  const handleChange = (target: string, val: number | number[]) => {
    const value = (val === -1)? null : val;

    setScores({
      ...scores,
      [target]: value,
    })
  }

  const calculateMocaScore = (scores: MocaScore): string => {
    // Check whether the score is of right type (because some are old types i.e. number only)
    if (scores && Number.isInteger(scores)) {
        return t("msg.format_need_update");
    } else if (scores && !Number.isInteger(scores) && scores.visuospatialExecutive === undefined) {
        return t("msg.invalid_record");
    }
    var total = 0;
    for (let [key, val] of Object.entries(scores)) {
      total += val;
    }
    return `${total} ${t("units_general.pt")}`;
  }

  return (
    <Dialog
      open={props.open}
      fullWidth
      onClose={props.onClose}
      maxWidth={"lg"}
    >
      <DialogTitle>{t("editMocaScorePanel.text_title_dialog")}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={8} style={{ width: "100%", margin: "0 auto", marginTop: 20 }}>
          <GridSliderWrapper backgroundImage={Q1} title={t("editMocaScorePanel.text_title_slider_1")}>
            <Slider
              valueLabelDisplay="on"
              value={scores.visuospatialExecutive ?? -1}
              valueLabelFormat={(value: number) => value === -1 ? "-" : value}
              // onChange={(e, v: number) => setScores(prev => { return { ...prev, "visuospatialExecutive": v === -1 ? null : v } })}
              onChange={(e, v) => handleChange("visuospatialExecutive", v)}
              step={1}
              marks={marks.slice(0, 7)}
              min={-1}
              max={5}
            />
          </GridSliderWrapper>

          <GridSliderWrapper backgroundImage={Q2} title={t("editMocaScorePanel.text_title_slider_2")}>
            <Slider
              value={scores.naming ?? -1}
              valueLabelDisplay="on"
              valueLabelFormat={(value: number) => value === -1 ? "-" : value}
              // onChange={(e, v: number) => setScores(prev => { return { ...prev, "naming": v === -1 ? null : v } })}
              onChange={(e, v) => handleChange("naming", v)}
              step={1}
              marks={marks.slice(0, 5)}
              min={-1}
              max={3}
            />
          </GridSliderWrapper>

          <GridSliderWrapper backgroundImage={Q4} title={t("editMocaScorePanel.text_title_slider_4")}>
            <Slider
              value={scores.attention ?? -1}
              valueLabelDisplay="on"
              valueLabelFormat={(value: number) => value === -1 ? "-" : value}
              // onChange={(e, v: number) => setScores(prev => { return { ...prev, "attention": v === -1 ? null : v } })}
              onChange={(e, v) => handleChange("attention", v)}
              step={1}
              marks={marks.slice(0, 8)}
              min={-1}
              max={6}
            />
          </GridSliderWrapper>

          <GridSliderWrapper backgroundImage={Q5} title={t("editMocaScorePanel.text_title_slider_5")}>
            <Slider
              value={scores.language ?? -1}
              valueLabelDisplay="on"
              valueLabelFormat={(value: number) => value === -1 ? "-" : value}
              // onChange={(e, v: number) => setScores(prev => { return { ...prev, "language": v === -1 ? null : v }})}
              onChange={(e, v) => handleChange("language", v)}
              step={1}
              marks={marks.slice(0, 5)}
              min={-1}
              max={3}
            />
          </GridSliderWrapper>

          <GridSliderWrapper backgroundImage={Q6} title={t("editMocaScorePanel.text_title_slider_6")}>
            <Slider
              value={scores.abstraction ?? -1}
              valueLabelDisplay="on"
              valueLabelFormat={(value: number) => value === -1 ? "-" : value}
              // onChange={(e, v) => setScores(prev => { return { ...prev, "abstraction": v === -1 ? null : v }})}
              onChange={(e, v) => handleChange("abstraction", v)}
              step={1}
              marks={marks.slice(0, 4)}
              min={-1}
              max={2}
            />
          </GridSliderWrapper>

          <GridSliderWrapper backgroundImage={Q3} title={t("editMocaScorePanel.text_title_slider_3")}>
            <Slider
              value={scores.delayedRecall ?? -1}
              valueLabelDisplay="on"
              valueLabelFormat={(value: number) => value === -1 ? "-" : value}
              // onChange={(e, v) => setScores(prev => { return { ...prev, "delayedRecall": v === -1 ? null : v }})}
              onChange={(e, v) => handleChange("delayedRecall", v)}
              step={1}
              marks={marks.slice(0, 7)}
              min={-1}
              max={5}
            />
          </GridSliderWrapper>

          <GridSliderWrapper backgroundImage={Q7} title={t("editMocaScorePanel.text_title_slider_7")}>
            <Slider
              value={scores.orientation ?? -1}
              valueLabelDisplay="on"
              valueLabelFormat={(value: number) => value === -1 ? "-" : value}
              // onChange={(e, v: number) => setScores(prev => { return { ...prev, "orientation": (v === -1) ? null : v }})}
              onChange={(e, v) => handleChange("orientation", v)}
              step={1}
              marks={marks.slice(0, 8)}
              min={-1}
              max={6}
            />
          </GridSliderWrapper>
        </Grid>
      </DialogContent>
      <DialogActions>
        <div style={{ marginRight: "auto", marginLeft: 10 }}>
          <span style={{ fontSize: 24 }}>
            {t("editMocaScorePanel.text_total_moca")}
          </span>
          <span style={{ fontSize: 24 }}>
            {calculateMocaScore(scores)}
          </span>
        </div>
        <Button disabled={Object.values(scores).some(v => v !== null)} variant="contained" disableElevation onClick={() => { props.onConfirm(null); props.onClose(); }} color="primary">
          {t("editMocaScorePanel.butt_moca_not_tested")}
        </Button>
        <Button disabled={!scores || Object.values(scores).some(v => v === null)} variant="contained" disableElevation onClick={() => { props.onConfirm(scores); props.onClose(); }} color="primary">
          {t("butt.save")}
        </Button>
        <Button variant="contained" disableElevation onClick={props.onClose} color="primary">
          {t("butt.close")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const useStyle: any = makeStyles((theme: Theme) => ({
  noMaxWidth: { maxWidth: "none" }
}));

const GridSliderWrapper = (props: { children: React.ReactNode, backgroundImage: string, title: string }) => {
  const classes = useStyle();

  return (
    <Grid
      item xs={12} sm={12} md={12} lg={6} xl={4}
      style={{ marginBottom: 10 }}
    >
      {props.children}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography gutterBottom variant="h6">
          {props.title}
        </Typography>
        <Tooltip title={<img src={props.backgroundImage} style={{ width: 600, maxWidth: "95vw" }} />} classes={{ tooltip: classes.noMaxWidth }}>
          <Info color="primary" />
        </Tooltip>
      </div>
    </Grid>
  )
}